import React, { useState, useEffect } from "react";
import { Form, Select, Input, Button, Tabs, message } from "antd";
import { UserOutlined, SearchOutlined } from "@ant-design/icons";
import { FaSearch, FaRocket, FaTimes } from "react-icons/fa";
import { getDistrictsByState, getReligionOptions } from "../../../Api/CoreApi"; // ✅ Add getReligionOptions
import {
  Hindu, Muslim, MuslimShia, MuslimSunni,
  Christian, Sikh, JainDigambar, JainShwetambar,
  Parsi, Buddhist, Jewish, InterReligion, NoReligiousBelief
} from 'indian-religion-caste'; // ✅ Add caste library
import "./SearchSection.css";

const { Option } = Select;
const { TabPane } = Tabs;

function SearchSection({
  searchValues,
  setSearchValues,
  searchIdValue,
  setSearchIdValue,
  availableStates,
  loading,
  isLoggedIn,
  currentUserGender,
  allUsers,
  setData,
}) {

  // ✅ Districts state
  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);

  // ✅ Religion & Caste states
  const [religionOptions, setReligionOptions] = useState([]);
  const [filteredCastes, setFilteredCastes] = useState([]);
  const [religionLoading, setReligionLoading] = useState(false);

  // ✅ Religion ke according caste map
  const casteMap = {
    'Hindu': Hindu,
    'Muslim': MuslimShia,
    'MuslimShia': MuslimShia,
    'MuslimSunni': MuslimSunni,
    'Christian': Christian,
    'Sikh': Sikh,
    'Jain': JainDigambar,
    'JainDigambar': JainDigambar,
    'JainShwetambar': JainShwetambar,
    'Buddhist': Buddhist,
    'Parsi': Parsi,
    'Jewish': Jewish,
    'InterReligion': InterReligion,
    'NoReligiousBelief': NoReligiousBelief,
  };

  // ✅ Page load pe religions fetch karo
  useEffect(() => {
    const loadReligions = async () => {
      try {
        setReligionLoading(true);
        const res = await getReligionOptions();
        setReligionOptions(res.religions || []);
      } catch (error) {
        console.error("Failed to load religions:", error);
        setReligionOptions([]);
      } finally {
        setReligionLoading(false);
      }
    };
    loadReligions();
  }, []);

  // ✅ Agar state already selected hai to districts load karo
  useEffect(() => {
    if (searchValues.state) {
      handleStateChange(searchValues.state);
    }
  }, []); // eslint-disable-line

  // ✅ Agar religion already selected hai to castes load karo
  useEffect(() => {
    if (searchValues.religion) {
      handleReligionChange(searchValues.religion);
    }
  }, []); // eslint-disable-line

  // Get gender info
  const getGenderFilterInfo = () => {
    if (!isLoggedIn || !currentUserGender) return null;
    const opposite = currentUserGender === 'Male' ? 'Female' : 'Male';
    return {
      showing: opposite,
      icon: opposite === 'Female' ? '👩' : '👨',
      color: opposite === 'Female' ? '#ff4d88' : '#2196f3'
    };
  };

  const genderInfo = getGenderFilterInfo();

  // Handle input change
  const handleInputChange = (field, value) => {
    setSearchValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ✅ State change handler
  const handleStateChange = async (value) => {
    handleInputChange('state', value || '');
    handleInputChange('district', '');
    setDistricts([]);

    if (!value) return;

    try {
      setDistrictsLoading(true);
      const response = await getDistrictsByState(value);
      const districtList =
        response?.districts ||
        (Array.isArray(response) ? response : []);
      setDistricts(districtList);
    } catch (error) {
      console.error("Districts load error:", error);
      message.error("Failed to load districts");
      setDistricts([]);
    } finally {
      setDistrictsLoading(false);
    }
  };

  // ✅ Religion change handler - caste filter karo
  const handleReligionChange = (value) => {
    handleInputChange('religion', value || '');
    handleInputChange('caste', ''); // ✅ Caste reset karo
    setFilteredCastes([]);

    if (!value) return;

    // ✅ Religion ke according castes set karo
    const castes = casteMap[value] || [];
    setFilteredCastes(castes);
  };

  // Manual Search by ID
  const searchbyid = async (values) => {
    let strval = values?.refer?.toString().trim() || searchIdValue.toString().trim();
    if (!strval) {
      message.warning("Please enter a Profile ID");
      return;
    }
    if (strval.toUpperCase().startsWith('CR')) {
      strval = strval.substring(2);
    }
    setSearchIdValue(strval);
  };

  // Manual Search
  const handleSearch = async () => {
    message.success("Filters applied!");
  };

  // Reset Filters
  const resetFilters = () => {
    const defaultGender = isLoggedIn && currentUserGender
      ? (currentUserGender === 'Male' ? 'Female' : 'Male')
      : '';

    setSearchValues({
      gender: defaultGender,
      minage: '',
      maxage: '',
      state: '',
      district: '',
      religion: '',
      caste: ''
    });

    // ✅ Sab reset karo
    setDistricts([]);
    setFilteredCastes([]);
    setSearchIdValue('');

    let filteredData = allUsers;
    if (isLoggedIn && currentUserGender) {
      const oppositeGender = currentUserGender === 'Male' ? 'Female' : 'Male';
      filteredData = allUsers.filter(user => user.gender === oppositeGender);
    }
    setData(filteredData);
    message.success("Filters cleared!");
  };

  return (
    <div className="search-section-luxury">
      <div className="search-container-luxury">

        {/* Header */}
        <div className="search-header-luxury">
          <h2 className="search-title-luxury">
            Find Your <span className="title-accent-luxury">Perfect Match</span>
          </h2>
          <p className="search-subtitle-luxury">
            {isLoggedIn && genderInfo ? (
              <span style={{ color: genderInfo.color, fontWeight: 700 }}>
                {genderInfo.icon} Showing {genderInfo.showing} profiles
              </span>
            ) : (
              "Search through thousands of verified profiles"
            )}
          </p>
        </div>

        {/* Tabs */}
        <Tabs
          defaultActiveKey="1"
          centered
          className="luxury-tabs"
          tabBarStyle={{ borderBottom: '2px solid #ffe4ec' }}
        >

          {/* TAB 1: SEARCH BY ID */}
          <TabPane
            tab={<span className="tab-label-luxury"><SearchOutlined /> Search by ID</span>}
            key="1"
          >
            <Form
              className="search-id-form-luxury"
              layout="inline"
              onFinish={searchbyid}
            >
              <Form.Item style={{ flex: 1, marginBottom: 0 }}>
                <Input
                  placeholder="Enter Profile ID (e.g., CR000001)"
                  prefix={<UserOutlined style={{ color: "#ff4d88" }} />}
                  className="luxury-input"
                  maxLength={20}
                  value={searchIdValue}
                  onChange={(e) => setSearchIdValue(e.target.value)}
                  allowClear
                />
              </Form.Item>
            </Form>
            <p className="search-hint-luxury">
              <FaRocket className="hint-icon" />
              Type Profile ID - results update automatically
            </p>
          </TabPane>

          {/* TAB 2: ADVANCE SEARCH */}
          <TabPane
            tab={<span className="tab-label-luxury"><FaSearch /> Advance Search</span>}
            key="2"
          >
            <div className="search-form-luxury">
              <div className="search-filters-grid-luxury">

                {/* Gender */}
                <div className="filter-item-luxury">
                  <label className="form-label-luxury">
                    <span className="label-icon">👤</span> Gender
                  </label>
                  <Select
                    placeholder="Select Gender"
                    allowClear
                    className="luxury-select"
                    value={searchValues.gender || undefined}
                    onChange={(value) => handleInputChange('gender', value || '')}
                  >
                    <Option value="Male">👨 Male</Option>
                    <Option value="Female">👩 Female</Option>
                  </Select>
                  {isLoggedIn && genderInfo && (
                    <div className="filter-hint" style={{ color: genderInfo.color }}>
                      💡 Default: {genderInfo.showing}
                    </div>
                  )}
                </div>

                {/* Age From */}
                <div className="filter-item-luxury">
                  <label className="form-label-luxury">
                    <span className="label-icon">🎂</span> Age From
                  </label>
                  <Input
                    type="number"
                    placeholder="Enter age"
                    className="luxury-input"
                    value={searchValues.minage}
                    onChange={(e) => handleInputChange('minage', e.target.value)}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value === '') return;
                      const numValue = parseInt(value);
                      if (numValue < 18) {
                        message.warning("⚠️ Minimum age is 18 years!");
                        handleInputChange('minage', '18');
                        return;
                      }
                      if (numValue > 70) {
                        message.warning("⚠️ Maximum age is 70 years!");
                        handleInputChange('minage', '70');
                        return;
                      }
                      if (searchValues.maxage && numValue > parseInt(searchValues.maxage)) {
                        message.warning("⚠️ Min age cannot be greater than Max age!");
                        handleInputChange('minage', searchValues.maxage);
                      }
                    }}
                    allowClear
                  />
                </div>

                {/* Age To */}
                <div className="filter-item-luxury">
                  <label className="form-label-luxury">
                    <span className="label-icon">🎯</span> Age To
                  </label>
                  <Input
                    type="number"
                    placeholder="Enter age"
                    className="luxury-input"
                    value={searchValues.maxage}
                    onChange={(e) => handleInputChange('maxage', e.target.value)}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value === '') return;
                      const numValue = parseInt(value);
                      if (numValue < 18) {
                        message.warning("⚠️ Minimum age is 18 years!");
                        handleInputChange('maxage', '18');
                        return;
                      }
                      if (numValue > 70) {
                        message.warning("⚠️ Maximum age is 70 years!");
                        handleInputChange('maxage', '70');
                        return;
                      }
                      if (searchValues.minage && numValue < parseInt(searchValues.minage)) {
                        message.warning("⚠️ Max age cannot be less than Min age!");
                        handleInputChange('maxage', searchValues.minage);
                      }
                    }}
                    allowClear
                  />
                </div>

                {/* State */}
                <div className="filter-item-luxury">
                  <label className="form-label-luxury">
                    <span className="label-icon">📍</span> State
                  </label>
                  <Select
                    placeholder="Select your state"
                    allowClear
                    className="luxury-select"
                    value={searchValues.state || undefined}
                    onChange={handleStateChange}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {availableStates.length > 0 ? (
                      availableStates.map((stateItem) => (
                        <Option key={stateItem} value={stateItem}>
                          {stateItem}
                        </Option>
                      ))
                    ) : (
                      <Option disabled value="">No states available</Option>
                    )}
                  </Select>
                </div>

                {/* District */}
                <div className="filter-item-luxury">
                  <label className="form-label-luxury">
                    <span className="label-icon">🏙️</span> District
                  </label>
                  <Select
                    placeholder={
                      !searchValues.state
                        ? "Select State first"
                        : districtsLoading
                          ? "Loading districts..."
                          : districts.length
                            ? "Select District"
                            : "No districts found"
                    }
                    allowClear
                    className="luxury-select"
                    value={searchValues.district || undefined}
                    onChange={(value) => handleInputChange('district', value || '')}
                    showSearch
                    disabled={!searchValues.state || districtsLoading}
                    loading={districtsLoading}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    notFoundContent={
                      districtsLoading ? "Loading..." : "No districts found"
                    }
                  >
                    {districts.map((district) => (
                      <Option key={district} value={district}>
                        {district}
                      </Option>
                    ))}
                  </Select>
                
                </div>

                {/* ✅ Religion - Admin set options se */}
                <div className="filter-item-luxury">
                  <label className="form-label-luxury">
                    <span className="label-icon">🙏</span> Religion
                  </label>
                  <Select
                    placeholder={
                      religionLoading
                        ? "Loading religions..."
                        : "Select Religion"
                    }
                    allowClear
                    className="luxury-select"
                    value={searchValues.religion || undefined}
                    onChange={handleReligionChange} // ✅ Updated handler
                    showSearch
                    loading={religionLoading}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children?.props?.children?.join('') || '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    notFoundContent={
                      religionLoading ? "Loading..." : "No religions found"
                    }
                  >
                    {/* ✅ Admin set religions dikhao */}
                    {religionOptions.map((r) => (
                      <Option key={r.id} value={r.value}>
                        <span>{r.emoji} {r.label}</span>
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* ✅ Caste - Religion ke according */}
                <div className="filter-item-luxury">
                  <label className="form-label-luxury">
                    <span className="label-icon">👥</span> Caste
                  </label>
                  {filteredCastes.length > 0 ? (
                    // ✅ Religion select hone par - Select dropdown
                    <Select
                      placeholder="Select or type caste"
                      allowClear
                      className="luxury-select"
                      value={searchValues.caste || undefined}
                      onChange={(value) => handleInputChange('caste', value || '')}
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                      }
                      notFoundContent={
                        <div style={{
                          padding: 8,
                          color: '#ff4d88',
                          fontSize: 12,
                          textAlign: 'center'
                        }}>
                          ✍️ Type to search caste
                        </div>
                      }
                    >
                      {filteredCastes.map((caste, index) => {
                        const casteName = caste.name || caste;
                        return (
                          <Option key={`${casteName}-${index}`} value={casteName}>
                            {casteName}
                          </Option>
                        );
                      })}
                    </Select>
                  ) : (
                    // ✅ Religion select nahi hua - Input field
                    <Input
                      placeholder={
                        searchValues.religion
                          ? "Type caste name..."
                          : "Select Religion first"
                      }
                      className="luxury-input"
                      value={searchValues.caste}
                      onChange={(e) => handleInputChange('caste', e.target.value)}
                      prefix={<SearchOutlined style={{ color: '#ff4d88' }} />}
                      allowClear
                    />
                  )}
                  {/* ✅ Hint */}
                  {!searchValues.religion && (
                    <div style={{ fontSize: 11, color: '#ff4d88', marginTop: 4 }}>
                      💡 Select religion to see caste options
                    </div>
                  )}
                  
                </div>

                {/* Buttons */}
                <div className="filter-actions-luxury">
                  <Button className="btn-clear-luxury" onClick={resetFilters}>
                    <FaTimes /> Clear All
                  </Button>
                  <Button
                    type="primary"
                    className="btn-search-luxury"
                    loading={loading}
                    onClick={handleSearch}
                  >
                    <FaSearch /> Search
                  </Button>
                </div>

              </div>
            </div>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
}

export default SearchSection;